/**
 * Minimal API client for FitBOXX.
 * Auto-detects whether to use Bearer (staff JWT) or MemberBearer (member JWT).
 */

import type { DeviceTrustInfo } from './passkey';

const API_PREFIX = '/api/v1';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface TokenStore {
  getStaffAccess(): string | null;
  getMemberAccess(): string | null;
}

const defaultStore: TokenStore = {
  getStaffAccess: () => localStorage.getItem('fitboxx.staff.access'),
  getMemberAccess: () => localStorage.getItem('fitboxx.member.access'),
};

let tokenStore: TokenStore = defaultStore;

export function configureTokenStore(store: Partial<TokenStore>) {
  tokenStore = { ...defaultStore, ...store };
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(ApiError.buildMessage(status, body));
  }
  private static buildMessage(status: number, body: unknown): string {
    // Try to surface the most useful piece of the response body so callers
    // that just read .message get something meaningful (DRF returns either
    // `{detail: "..."}` or a per-field map like `{field: ["msg"]}`).
    if (body && typeof body === 'object') {
      const b = body as Record<string, unknown>;
      if (typeof b.detail === 'string') return `${b.detail} (HTTP ${status})`;
      const firstField = Object.entries(b)[0];
      if (firstField) {
        const [k, v] = firstField;
        const msg = Array.isArray(v) ? String(v[0]) : String(v);
        return `${k}: ${msg} (HTTP ${status})`;
      }
    }
    if (typeof body === 'string' && body) return `${body.slice(0, 120)} (HTTP ${status})`;
    return `HTTP ${status}`;
  }
}

// Endpoints that are expected to return 401 when the visitor has no
// session — calling these is part of probing whether a session exists,
// so we MUST NOT auto-logout on their 401. The refresh endpoints
// themselves are also here so a 401 on refresh doesn't trigger another
// refresh (loop).
const NO_AUTO_LOGOUT_PATHS = new Set<string>([
  '/auth/staff/login/',
  '/auth/staff/refresh/',
  '/auth/staff/forgot/',
  '/auth/staff/sso/start/',
  '/auth/staff/sso/accept/',
  '/auth/member/otp/request/',
  '/auth/member/otp/verify/',
  '/auth/member/refresh/',
]);

// ── Single-flight token refresh ────────────────────────────────────────────
//
// SimpleJWT on the backend runs with ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION. Every successful refresh burns the old
// refresh token. Without single-flighting, parallel 401s each fire their
// own refresh — only the first wins, the rest hit "token blacklisted"
// and yank the user to /login. We share one in-flight refresh per token
// mode, and capture the rotated refresh from the response.
//
// Until this lived in the codebase, staff users were kicked out every
// 60 minutes (ACCESS_TOKEN_LIFETIME).

let staffRefreshPromise: Promise<string> | null = null;
let memberRefreshPromise: Promise<string> | null = null;

async function _performRefresh(asMember: boolean): Promise<string> {
  const refreshKey = asMember ? 'fitboxx.member.refresh' : 'fitboxx.staff.refresh';
  const accessKey = asMember ? 'fitboxx.member.access' : 'fitboxx.staff.access';
  const refreshUrl = asMember
    ? `${API_PREFIX}/auth/member/refresh/`
    : `${API_PREFIX}/auth/staff/refresh/`;
  const refresh = localStorage.getItem(refreshKey);
  if (!refresh) throw new Error('no_refresh_token');
  const res = await fetch(refreshUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`refresh_failed_${res.status}`);
  const data = await res.json();
  if (!data?.access) throw new Error('refresh_response_missing_access');
  localStorage.setItem(accessKey, data.access);
  if (data.refresh) localStorage.setItem(refreshKey, data.refresh);
  return data.access as string;
}

function refreshAccessToken(asMember: boolean): Promise<string> {
  const slot = asMember ? memberRefreshPromise : staffRefreshPromise;
  if (slot) return slot;
  const p = _performRefresh(asMember).finally(() => {
    if (asMember) memberRefreshPromise = null;
    else staffRefreshPromise = null;
  });
  if (asMember) memberRefreshPromise = p;
  else staffRefreshPromise = p;
  return p;
}

function handle401(path: string, asMember: boolean) {
  if (NO_AUTO_LOGOUT_PATHS.has(path)) return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/login')) return;
  if (asMember) {
    localStorage.removeItem('fitboxx.member.access');
    localStorage.removeItem('fitboxx.member.refresh');
  } else {
    localStorage.removeItem('fitboxx.staff.access');
    localStorage.removeItem('fitboxx.staff.refresh');
    localStorage.removeItem('fitboxx.staff.profile');
  }
  try {
    sessionStorage.setItem('fitboxx.post_login_return', window.location.pathname + window.location.search);
  } catch { /* sessionStorage may be unavailable in private mode */ }
  window.location.replace('/login');
}

// 402 `subscription_inactive` = this studio's FitBOXX subscription is
// suspended in SaaSBOXX (backend core.middleware entitlement gate). Every
// other call on this host will fail the same way, so a signed-in session is
// ended — staff and member alike, they share this origin — and the app's own
// sign-in page says why. Other 402s (the AI budget's buy-more prompt) are not
// this and are left to their callers. With no session there is nothing to
// end: the sign-in pages, SSO landing and public widget show the error where
// they are.
const SESSION_KEYS = [
  'fitboxx.staff.access', 'fitboxx.staff.refresh', 'fitboxx.staff.profile',
  'fitboxx.member.access', 'fitboxx.member.refresh',
];

function handleSubscriptionInactive(status: number, data: unknown) {
  if (status !== 402) return;
  if ((data as { error?: unknown } | null)?.error !== 'subscription_inactive') return;
  if (typeof window === 'undefined') return;
  const hadSession = SESSION_KEYS.some((k) => localStorage.getItem(k));
  SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
  if (!hadSession) return;
  // The admin console is mounted at /app/ (vite base + router basename); the
  // member portal at /. Each renders its sign-in page for any path once the
  // tokens are gone, and reads ?message= from there.
  const path = window.location.pathname;
  const login = path === '/app' || path.startsWith('/app/') ? '/app/login' : '/login';
  if (path.startsWith(login)) return;
  window.location.replace(`${login}?message=subscription_inactive`);
}

async function _doFetch(path: string, init: { method?: Method; body?: unknown; asMember?: boolean; _retry?: boolean }) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (init.asMember) {
    const t = tokenStore.getMemberAccess();
    if (t) headers['Authorization'] = `MemberBearer ${t}`;
  } else {
    const t = tokenStore.getStaffAccess();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  return fetch(`${API_PREFIX}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    credentials: 'include',
  });
}

export async function api<T = unknown>(
  path: string,
  init: { method?: Method; body?: unknown; asMember?: boolean; _retry?: boolean } = {}
): Promise<T> {
  let res = await _doFetch(path, init);

  // 401 → attempt a single-flight refresh once, then retry the original.
  // Skip on the refresh endpoint itself, on probe endpoints, and if we
  // already retried.
  if (res.status === 401 && !init._retry && !NO_AUTO_LOGOUT_PATHS.has(path)) {
    const asMember = !!init.asMember;
    const refreshKey = asMember ? 'fitboxx.member.refresh' : 'fitboxx.staff.refresh';
    if (typeof window !== 'undefined' && localStorage.getItem(refreshKey)) {
      try {
        await refreshAccessToken(asMember);
        res = await _doFetch(path, { ...init, _retry: true });
      } catch {
        // Refresh failed (token expired / blacklisted) — fall through to handle401
      }
    }
  }

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = res.ok ? text : { detail: `HTTP ${res.status} ${res.statusText || ''}`.trim() };
    }
  }
  if (!res.ok) {
    if (res.status === 401) handle401(path, !!init.asMember);
    handleSubscriptionInactive(res.status, data);
    throw new ApiError(res.status, data);
  }
  return data as T;
}

/**
 * Fetch a binary asset (PDF / image / CSV) that lives behind an
 * authenticated endpoint. Returns a same-origin `Blob` so callers can
 * pipe it into `<iframe src=...>`, `window.open`, or an `<a download>`.
 *
 * Use this for any endpoint we want to open in a new tab — `window.open`
 * does NOT include `Authorization: Bearer …` headers because the
 * resulting navigation is a fresh document request without our fetch
 * interceptor, so endpoints behind staff/member JWT return 401.
 */
export async function fetchBlob(
  path: string,
  init: { asMember?: boolean } = {},
): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (init.asMember) {
    const t = tokenStore.getMemberAccess();
    if (t) headers['Authorization'] = `MemberBearer ${t}`;
  } else {
    const t = tokenStore.getStaffAccess();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  // `path` may already be absolute (starts with /api/v1/...) for callers
  // that hold the full URL; preserve it as-is in that case.
  const url = path.startsWith('/api/') ? path : `${API_PREFIX}${path}`;
  const res = await fetch(url, { headers, credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401) handle401(path, !!init.asMember);
    throw new ApiError(res.status, await res.text().catch(() => ''));
  }
  return res.blob();
}

/**
 * Open or download an authenticated PDF (or any blob-yielding endpoint).
 *
 * - `mode: 'preview'` → opens the blob URL in a new tab (works because
 *   the blob URL is same-origin and needs no auth).
 * - `mode: 'download'` → triggers an `<a download="...">` save.
 */
export async function openAuthedPdf(
  path: string,
  opts: { mode: 'preview' | 'download'; filename?: string; asMember?: boolean },
): Promise<void> {
  const blob = await fetchBlob(path, { asMember: opts.asMember });
  // Force a PDF MIME type so Chrome's PDF viewer kicks in on preview, even
  // if the server returned `application/octet-stream` on a download response.
  const typed = blob.type === 'application/pdf'
    ? blob
    : new Blob([blob], { type: 'application/pdf' });
  const url = URL.createObjectURL(typed);
  if (opts.mode === 'download') {
    const a = document.createElement('a');
    a.href = url;
    a.download = opts.filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } else {
    window.open(url, '_blank');
    // Browsers hold the new tab open against the URL; revoke after a minute
    // to free memory but well after the PDF viewer has rendered.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

// Convenience wrappers
export const widget = {
  tenant: () => api('/widget/tenant/'),
  schedule: (days = 14) => api(`/widget/schedule/?days=${days}`),
  packages: () => api('/widget/packages/'),
  classTemplates: () => api('/widget/class-templates/'),
};

// GateBOXX passkey (WebAuthn) step-up fields, optional on the login/verify body.
// A require_passkey=required tenant makes the resubmit carry the
// navigator.credentials.get() result plus the login host; inert (absent) for
// every tenant on require_passkey=off.
interface PasskeyBody {
  passkey_assertion?: unknown;
  rp_id?: string;
  origin?: string;
}

export const memberAuth = {
  requestOTP: (body: { email?: string; phone?: string }) =>
    api('/auth/member/otp/request/', { method: 'POST', body }),
  verifyOTP: (body: {
    email?: string; phone?: string; code: string;
    first_name?: string; last_name?: string;
  } & PasskeyBody) =>
    api<{ access: string; device_trust?: DeviceTrustInfo }>(
      '/auth/member/otp/verify/', { method: 'POST', body }),
  me: () => api('/auth/member/me/', { asMember: true }),
};

export const staffAuth = {
  login: (body: { email: string; password: string; totp_code?: string } & PasskeyBody) =>
    api<{ access: string; refresh: string; profile: unknown; device_trust?: DeviceTrustInfo }>(
      '/auth/staff/login/', { method: 'POST', body }),
  forgot: (body: { email: string }) =>
    api<{ sent: boolean }>('/auth/staff/forgot/', { method: 'POST', body }),
  ssoStart: (return_url: string) =>
    api<{ authorize_url: string }>('/auth/staff/sso/start/', { method: 'POST', body: { return_url } }),
  /** SaasBOXX-as-IdP handshake — exchange the federation token for our own JWT. */
  ssoAccept: (token: string) =>
    api<{
      access: string;
      refresh: string;
      profile: { email: string; first_name: string; last_name: string; display_name: string; role: string; is_superuser: boolean };
      tenant: { slug: string; display_name: string };
      frontend_url: string;
      provisioned: boolean;
      profile_created: boolean;
    }>('/auth/staff/sso/accept/', { method: 'POST', body: { token } }),
  me: () => api('/auth/staff/me/'),
};

export const memberPortal = {
  // Profile
  me: () => api('/auth/member/me/', { asMember: true }),

  // Bookings
  bookings: () => api('/schedule/member/me/bookings/', { asMember: true }),
  history: () => api('/schedule/member/me/bookings/history/', { asMember: true }),
  upcomingClasses: (days = 14) =>
    api(`/schedule/member/me/bookings/upcoming-classes/?days=${days}`, { asMember: true }),
  book: (occurrenceId: string) =>
    api(`/schedule/member/me/bookings/${occurrenceId}/book/`, { method: 'POST', asMember: true }),
  cancel: (bookingId: string) =>
    api(`/schedule/member/me/bookings/${bookingId}/cancel/`, { method: 'POST', asMember: true }),

  // Wallet
  wallet: () => api('/wallet/member/me/', { asMember: true }),

  // Catalog (public read, member token works)
  packages: () => api('/catalog/packages/', { asMember: true }),
  membershipPlans: () => api('/catalog/membership-plans/', { asMember: true }),

  // Invoices
  invoices: () => api('/payments/member/me/invoices/', { asMember: true }),
  payInvoice: (id: string, body: { provider?: string; return_url?: string; cancel_url?: string }) =>
    api(`/payments/member/me/invoices/${id}/pay/`, { method: 'POST', body, asMember: true }),
  // Member-scoped PDF route; fetch it with openAuthedPdf(..., { asMember: true }).
  invoicePdfUrl: (id: string, download = false) =>
    `/payments/member/me/invoices/${id}/pdf/${download ? '?download=true' : ''}`,

  // Agreements / waivers
  waiverStatus: () => api('/crm/member-waivers/status/', { asMember: true }),
  signWaiver: (body: { typed_name?: string }) =>
    api('/crm/member-waivers/sign/', { method: 'POST', body, asMember: true }),

  // Checkout
  checkout: (body: unknown) =>
    api('/payments/checkout/', { method: 'POST', body, asMember: true }),

  // My progress — own health readings, goals and adherence. The member id
  // is never sent: the backend resolves it from the token.
  progress: (days = 365) =>
    api(`/progress/member/me/?days=${days}`, { asMember: true }),
  progressMetrics: () => api('/progress/member/me/metrics/', { asMember: true }),
  logMeasurement: (body: { metric: string; value: number | string; recorded_at?: string; notes?: string }) =>
    api('/progress/member/me/log/', { method: 'POST', body, asMember: true }),
};

/**
 * Set or remove the photo of a package or membership plan: a multipart PATCH
 * of the row itself, so it is permission-checked exactly like any other edit
 * to it. The ordinary JSON save never changes the photo (the serializer drops
 * it), which is why this is a call of its own. An empty `image` removes it.
 */
async function sendCatalogImage(path: string, image: File | ''): Promise<{ image: string | null }> {
  const send = () => {
    const fd = new FormData();
    fd.append('image', image);
    const t = tokenStore.getStaffAccess();
    return fetch(`${API_PREFIX}${path}`, {
      method: 'PATCH',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
      credentials: 'include',
    });
  };
  let res = await send();
  // The same single-flight refresh-and-retry as api(): a page left open past
  // the access token's lifetime must not fail every upload until some other
  // call happens to refresh the token.
  if (res.status === 401 && typeof window !== 'undefined'
      && localStorage.getItem('fitboxx.staff.refresh')) {
    try {
      await refreshAccessToken(false);
      res = await send();
    } catch {
      // Refresh failed -- fall through to the 401 handling below.
    }
  }
  const data = (await res.json().catch(() => null)) as
    { image?: string | string[] | null; detail?: string } | null;
  if (!res.ok) {
    if (res.status === 401) handle401(path, false);
    // A proxy answers an oversize body with an HTML 413 before Django sees it.
    if (res.status === 413) throw new Error('That photo is too large to upload.');
    // No "Upload failed:" here -- the caller says which action failed.
    const reason = Array.isArray(data?.image) ? data?.image[0] : data?.detail;
    throw new Error(reason || `HTTP ${res.status}`);
  }
  return { image: typeof data?.image === 'string' ? data.image : null };
}

export const adminApi = {
  occurrences: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/schedule/occurrences/${q ? `?${q}` : ''}`);
  },
  createOccurrence: (body: unknown) =>
    api('/schedule/occurrences/', { method: 'POST', body }),
  classTemplates: () => api('/catalog/class-templates/'),

  // Danger Zone — tenant data reset (owner/admin). dry_run returns the plan.
  clearTenantData: (body: { scope: string; dry_run?: boolean; confirm?: string }) =>
    api('/tenants/danger-zone/clear-data/', { method: 'POST', body }),

  // Members CRUD (per-tenant, in crm app)
  members: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/crm/members/${q ? `?${q}` : ''}`);
  },
  member: (id: string) => api(`/crm/members/${id}/`),
  createMember: (body: unknown) =>
    api('/crm/members/', { method: 'POST', body }),
  updateMember: (id: string, body: unknown) =>
    api(`/crm/members/${id}/`, { method: 'PATCH', body }),
  deleteMember: (id: string) =>
    api(`/crm/members/${id}/`, { method: 'DELETE' }),

  // Staff CRUD (central, in identity app)
  // Takes params like the other list endpoints: a picker that silently
  // showed only the first page (DRF PAGE_SIZE = 50) would omit people from
  // an assignee dropdown without ever saying so.
  staff: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/auth/staff/${q ? `?${q}` : ''}`);
  },
  staffMember: (id: string) => api(`/auth/staff/${id}/`),
  createStaff: (body: unknown) =>
    api('/auth/staff/', { method: 'POST', body }),
  updateStaff: (id: string, body: unknown) =>
    api(`/auth/staff/${id}/`, { method: 'PATCH', body }),
  deleteStaff: (id: string) =>
    api(`/auth/staff/${id}/`, { method: 'DELETE' }),
  resetStaffPassword: (id: string) =>
    api(`/auth/staff/${id}/reset-password/`, { method: 'POST', body: {} }),
  reactivateStaff: (id: string) =>
    api(`/auth/staff/${id}/reactivate/`, { method: 'POST', body: {} }),

  // Self check-in kiosks (premium add-on)
  kiosks: () => api('/biometric/kiosks/'),
  createKiosk: (body: { name: string; location?: string | null; notes?: string }) =>
    api('/biometric/kiosks/', { method: 'POST', body }),
  regenerateKioskPairCode: (id: string) =>
    api(`/biometric/kiosks/${id}/regenerate-pair-code/`, { method: 'POST', body: {} }),
  revokeKiosk: (id: string) =>
    api(`/biometric/kiosks/${id}/revoke/`, { method: 'POST', body: {} }),
  deleteKiosk: (id: string) =>
    api(`/biometric/kiosks/${id}/`, { method: 'DELETE' }),
  setMemberKioskPin: (memberId: string, pin: string) =>
    api(`/crm/members/${memberId}/kiosk-pin/`, { method: 'POST', body: { pin } }),

  // Member 360 — KPI rollups, activity feed, agreements
  memberSummary: (memberId: string) => api(`/crm/members/${memberId}/summary/`),
  memberActivity: (memberId: string) => api(`/crm/members/${memberId}/activity/`),
  memberAgreements: (memberId: string) => api(`/crm/members/${memberId}/agreements/`),

  // Service agreements (commercial contracts — distinct from waivers)
  serviceAgreementsForMember: (memberId: string) =>
    api(`/crm/service-agreements/?member=${memberId}`),
  serviceAgreement: (id: string) => api(`/crm/service-agreements/${id}/`),
  createServiceAgreement: (body: unknown) =>
    api('/crm/service-agreements/', { method: 'POST', body }),
  updateServiceAgreement: (id: string, body: unknown) =>
    api(`/crm/service-agreements/${id}/`, { method: 'PATCH', body }),
  deleteServiceAgreement: (id: string) =>
    api(`/crm/service-agreements/${id}/`, { method: 'DELETE' }),
  sendServiceAgreement: (id: string) =>
    api(`/crm/service-agreements/${id}/send/`, { method: 'POST', body: {} }),
  uploadServiceAgreementDocument: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('document', file);
    const t = localStorage.getItem('fitboxx.staff.access');
    const res = await fetch(`/api/v1/crm/service-agreements/${id}/`, {
      method: 'PATCH',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    return res.json();
  },

  // Tasks
  tasksForMember: (memberId: string) => api(`/crm/tasks/?member=${memberId}`),
  createTask: (body: unknown) => api('/crm/tasks/', { method: 'POST', body }),
  updateTask: (id: string, body: unknown) =>
    api(`/crm/tasks/${id}/`, { method: 'PATCH', body }),
  deleteTask: (id: string) => api(`/crm/tasks/${id}/`, { method: 'DELETE' }),

  // Member files
  memberFiles: (memberId: string) => api(`/crm/member-files/?member=${memberId}`),
  deleteMemberFile: (id: string) => api(`/crm/member-files/${id}/`, { method: 'DELETE' }),
  uploadMemberFile: async (memberId: string, file: File, label = '', description = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('member', memberId);
    if (label) fd.append('label', label);
    if (description) fd.append('description', description);
    const t = localStorage.getItem('fitboxx.staff.access');
    const res = await fetch('/api/v1/crm/member-files/', {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    return res.json();
  },

  // Invoices scoped by member (uses existing invoices endpoint with filter)
  invoicesForMember: (memberId: string) => api(`/payments/invoices/?member=${memberId}`),
  subscriptionsForMember: (memberId: string) =>
    api(`/payments/subscriptions/?member=${memberId}`),
  consultationsForMember: (memberId: string) =>
    api(`/wellness/consultations/?member=${memberId}`),

  dashboardToday: () => api('/studio/dashboard/today/'),
  reportAttendance: (days = 30) => api(`/studio/reports/attendance/?days=${days}`),
  reportRevenue: (days = 30) => api(`/studio/reports/revenue/?days=${days}`),

  // Catalog — Membership plans CRUD
  membershipPlans: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/catalog/membership-plans/${q ? `?${q}` : ''}`);
  },
  membershipPlan: (id: string) => api(`/catalog/membership-plans/${id}/`),
  createMembershipPlan: (body: unknown) => api('/catalog/membership-plans/', { method: 'POST', body }),
  updateMembershipPlan: (id: string, body: unknown) => api(`/catalog/membership-plans/${id}/`, { method: 'PATCH', body }),
  deleteMembershipPlan: (id: string) => api(`/catalog/membership-plans/${id}/`, { method: 'DELETE' }),
  uploadMembershipPlanImage: (id: string, file: File) => sendCatalogImage(`/catalog/membership-plans/${id}/`, file),
  removeMembershipPlanImage: (id: string) => sendCatalogImage(`/catalog/membership-plans/${id}/`, ''),

  // Catalog — Packages CRUD
  packages: () => api('/catalog/packages/'),
  package_: (id: string) => api(`/catalog/packages/${id}/`),
  createPackage: (body: unknown) => api('/catalog/packages/', { method: 'POST', body }),
  updatePackage: (id: string, body: unknown) => api(`/catalog/packages/${id}/`, { method: 'PATCH', body }),
  deletePackage: (id: string) => api(`/catalog/packages/${id}/`, { method: 'DELETE' }),
  uploadPackageImage: (id: string, file: File) => sendCatalogImage(`/catalog/packages/${id}/`, file),
  removePackageImage: (id: string) => sendCatalogImage(`/catalog/packages/${id}/`, ''),

  // Catalog — Promo codes CRUD
  promoCodes: () => api('/catalog/promo-codes/'),
  promoCode: (id: string) => api(`/catalog/promo-codes/${id}/`),
  createPromoCode: (body: unknown) => api('/catalog/promo-codes/', { method: 'POST', body }),
  updatePromoCode: (id: string, body: unknown) => api(`/catalog/promo-codes/${id}/`, { method: 'PATCH', body }),
  deletePromoCode: (id: string) => api(`/catalog/promo-codes/${id}/`, { method: 'DELETE' }),

  // Catalog — Class templates CRUD
  classTemplate: (id: string) => api(`/catalog/class-templates/${id}/`),
  createClassTemplate: (body: unknown) => api('/catalog/class-templates/', { method: 'POST', body }),
  updateClassTemplate: (id: string, body: unknown) => api(`/catalog/class-templates/${id}/`, { method: 'PATCH', body }),
  deleteClassTemplate: (id: string) => api(`/catalog/class-templates/${id}/`, { method: 'DELETE' }),

  // Catalog — Session types CRUD (Trainer mode)
  sessionTypes: () => api('/catalog/session-types/'),
  sessionType: (id: string) => api(`/catalog/session-types/${id}/`),
  createSessionType: (body: unknown) => api('/catalog/session-types/', { method: 'POST', body }),
  updateSessionType: (id: string, body: unknown) => api(`/catalog/session-types/${id}/`, { method: 'PATCH', body }),
  deleteSessionType: (id: string) => api(`/catalog/session-types/${id}/`, { method: 'DELETE' }),

  // Catalog — Appointment types CRUD (Wellness mode)
  appointmentTypes: () => api('/catalog/appointment-types/'),
  appointmentType: (id: string) => api(`/catalog/appointment-types/${id}/`),
  createAppointmentType: (body: unknown) => api('/catalog/appointment-types/', { method: 'POST', body }),
  updateAppointmentType: (id: string, body: unknown) => api(`/catalog/appointment-types/${id}/`, { method: 'PATCH', body }),
  deleteAppointmentType: (id: string) => api(`/catalog/appointment-types/${id}/`, { method: 'DELETE' }),

  // Appointment engine — operational side (Wellness + Salon modes):
  // day board, slot search, visit lifecycle, staff availability.
  appointmentBook: (date: string) => api(`/appointments/appointments/book/?date=${date}`),
  appointmentSlots: (typeIds: string[], date: string, practitioner?: string) =>
    api(`/appointments/appointments/slots/?types=${typeIds.join(',')}&date=${date}` +
        (practitioner ? `&practitioner=${practitioner}` : '')),
  appointmentsList: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/appointments/appointments/${q ? `?${q}` : ''}`);
  },
  createAppointment: (body: unknown) => api('/appointments/appointments/', { method: 'POST', body }),
  updateAppointment: (id: string, body: unknown) =>
    api(`/appointments/appointments/${id}/`, { method: 'PATCH', body }),
  appointmentAction: (id: string, action: string, body: unknown = {}) =>
    api(`/appointments/appointments/${id}/${action}/`, { method: 'POST', body }),
  checkoutAppointment: (id: string, body: unknown) =>
    api(`/appointments/appointments/${id}/checkout/`, { method: 'POST', body }),
  commissionsReport: (dateFrom: string, dateTo: string) =>
    api(`/appointments/appointments/reports/commissions/?date_from=${dateFrom}&date_to=${dateTo}`),
  retailProducts: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/catalog/retail-products/${q ? `?${q}` : ''}`);
  },
  createRetailProduct: (body: unknown) => api('/catalog/retail-products/', { method: 'POST', body }),
  updateRetailProduct: (id: string, body: unknown) =>
    api(`/catalog/retail-products/${id}/`, { method: 'PATCH', body }),
  adjustRetailStock: (id: string, delta: number, reason = '') =>
    api(`/catalog/retail-products/${id}/adjust-stock/`, { method: 'POST', body: { delta, reason } }),
  staffAvailability: (staffId?: string) =>
    api(`/appointments/availability/${staffId ? `?staff=${staffId}` : ''}`),
  createStaffAvailability: (body: unknown) => api('/appointments/availability/', { method: 'POST', body }),
  deleteStaffAvailability: (id: string) => api(`/appointments/availability/${id}/`, { method: 'DELETE' }),
  availabilityOverrides: (staffId?: string) =>
    api(`/appointments/availability-overrides/${staffId ? `?staff=${staffId}` : ''}`),
  createAvailabilityOverride: (body: unknown) =>
    api('/appointments/availability-overrides/', { method: 'POST', body }),
  deleteAvailabilityOverride: (id: string) =>
    api(`/appointments/availability-overrides/${id}/`, { method: 'DELETE' }),

  // Catalog — Service categories CRUD
  serviceCategories: () => api('/catalog/categories/'),
  serviceCategory: (id: string) => api(`/catalog/categories/${id}/`),
  // Detail-page fetch wrappers are listed earlier; keep this group for legacy lookups.
  createServiceCategory: (body: unknown) => api('/catalog/categories/', { method: 'POST', body }),
  updateServiceCategory: (id: string, body: unknown) => api(`/catalog/categories/${id}/`, { method: 'PATCH', body }),
  deleteServiceCategory: (id: string) => api(`/catalog/categories/${id}/`, { method: 'DELETE' }),
  resetServiceCategoriesToMaster: () => api('/catalog/categories/reset-to-master/', { method: 'POST' }),

  // Resources — Room types CRUD + reset-to-master
  roomTypes: () => api('/resources/room-types/'),
  roomType: (id: string) => api(`/resources/room-types/${id}/`),
  createRoomType: (body: unknown) => api('/resources/room-types/', { method: 'POST', body }),
  updateRoomType: (id: string, body: unknown) => api(`/resources/room-types/${id}/`, { method: 'PATCH', body }),
  deleteRoomType: (id: string) => api(`/resources/room-types/${id}/`, { method: 'DELETE' }),
  resetRoomTypesToMaster: () => api('/resources/room-types/reset-to-master/', { method: 'POST' }),

  // Resources — Rooms / Equipment / Equipment types CRUD (list getters live in the Resources section below)
  room: (id: string) => api(`/resources/rooms/${id}/`),
  createRoom: (body: unknown) => api('/resources/rooms/', { method: 'POST', body }),
  updateRoom: (id: string, body: unknown) => api(`/resources/rooms/${id}/`, { method: 'PATCH', body }),
  deleteRoom: (id: string) => api(`/resources/rooms/${id}/`, { method: 'DELETE' }),
  equipmentType: (id: string) => api(`/resources/equipment-types/${id}/`),
  createEquipmentType: (body: unknown) => api('/resources/equipment-types/', { method: 'POST', body }),
  updateEquipmentType: (id: string, body: unknown) => api(`/resources/equipment-types/${id}/`, { method: 'PATCH', body }),
  deleteEquipmentType: (id: string) => api(`/resources/equipment-types/${id}/`, { method: 'DELETE' }),
  equipmentItem: (id: string) => api(`/resources/equipment/${id}/`),
  createEquipment: (body: unknown) => api('/resources/equipment/', { method: 'POST', body }),
  updateEquipment: (id: string, body: unknown) => api(`/resources/equipment/${id}/`, { method: 'PATCH', body }),
  deleteEquipment: (id: string) => api(`/resources/equipment/${id}/`, { method: 'DELETE' }),

  // Marketing — Leads + Campaigns CRUD (list getters live in the Marketing section below)
  lead: (id: string) => api(`/marketing/leads/${id}/`),
  createLead: (body: unknown) => api('/marketing/leads/', { method: 'POST', body }),
  updateLead: (id: string, body: unknown) => api(`/marketing/leads/${id}/`, { method: 'PATCH', body }),
  deleteLead: (id: string) => api(`/marketing/leads/${id}/`, { method: 'DELETE' }),
  // Creates the Member record (as a prospect unless `activate`) and links it back.
  convertLead: (id: string, body: { activate?: boolean } = {}) =>
    api(`/marketing/leads/${id}/convert/`, { method: 'POST', body }),
  campaign: (id: string) => api(`/marketing/campaigns/${id}/`),
  createCampaign: (body: unknown) => api('/marketing/campaigns/', { method: 'POST', body }),
  updateCampaign: (id: string, body: unknown) => api(`/marketing/campaigns/${id}/`, { method: 'PATCH', body }),
  deleteCampaign: (id: string) => api(`/marketing/campaigns/${id}/`, { method: 'DELETE' }),

  // Payments
  orders: () => api('/payments/orders/'),
  invoices: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/payments/invoices/${q ? `?${q}` : ''}`);
  },
  invoiceDetail: (id: string) => api(`/payments/invoices/${id}/`),
  invoicePdfUrl: (id: string, download = false) =>
    `/api/v1/payments/invoices/${id}/pdf/${download ? '?download=true' : ''}`,
  invoiceHtml: (id: string) => api<string | null>(`/payments/invoices/${id}/html/`),
  refundInvoice: (id: string, body: {
    amount?: number | string; method?: string; reason?: string; reference?: string;
  }) => api(`/payments/invoices/${id}/refund/`, { method: 'POST', body }),
  issueCreditNote: (id: string, body: {
    amount?: number | string; reason?: string; items?: unknown[];
  }) => api(`/payments/invoices/${id}/credit-note/`, { method: 'POST', body }),
  runDunning: () => api('/payments/dunning/run/', { method: 'POST', body: {} }),

  // Wallet
  walletForMember: (memberId: string) => api(`/wallet/admin/?member=${memberId}`),
  walletAdminList: () => api('/wallet/admin/'),
  walletTransactions: (walletId: string) => api(`/wallet/admin/${walletId}/transactions/`),
  grantCredits: (body: unknown) => api('/wallet/admin/grant/', { method: 'POST', body }),

  // Schedule — admin actions on occurrences + bookings
  cancelOccurrence: (id: string, reason: string) =>
    api(`/schedule/occurrences/${id}/cancel/`, { method: 'POST', body: { reason } }),
  occurrenceRoster: (id: string) => api(`/schedule/occurrences/${id}/roster/`),
  updateOccurrence: (id: string, body: unknown) =>
    api(`/schedule/occurrences/${id}/`, { method: 'PATCH', body }),
  checkInBooking: (id: string) =>
    api(`/schedule/bookings/${id}/check_in/`, { method: 'POST', body: {} }),
  cancelBooking: (id: string) =>
    api(`/schedule/bookings/${id}/staff_cancel/`, { method: 'POST', body: {} }),

  // Schedule — bookings by member (for the Member drawer)
  bookingsForMember: (memberId: string) => api(`/schedule/bookings/?member=${memberId}`),

  // Schedule — recurring series CRUD
  series: () => api('/schedule/series/'),
  createSeries: (body: unknown) => api('/schedule/series/', { method: 'POST', body }),
  updateSeries: (id: string, body: unknown) =>
    api(`/schedule/series/${id}/`, { method: 'PATCH', body }),
  deleteSeries: (id: string) => api(`/schedule/series/${id}/`, { method: 'DELETE' }),

  // Wellness
  consultations: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/wellness/consultations/${q ? `?${q}` : ''}`);
  },
  consultation: (id: string) => api(`/wellness/consultations/${id}/`),
  healthProfiles: () => api('/wellness/health-profiles/'),
  consultationStats: () => api('/wellness/consultations/stats/'),

  // Marketing
  campaigns: () => api('/marketing/campaigns/'),
  leads: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/marketing/leads/${q ? `?${q}` : ''}`);
  },
  leadStats: () => api('/marketing/leads/stats/'),

  // Notifications — inbox
  notifications: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/notifications/${q ? `?${q}` : ''}`);
  },
  notificationsUnreadCount: () => api('/notifications/unread_count/'),
  notificationsRecent: () => api('/notifications/recent/'),
  markNotificationRead: (id: string) => api(`/notifications/${id}/mark_read/`, { method: 'POST' }),
  markAllNotificationsRead: () => api('/notifications/mark_all_read/', { method: 'POST' }),
  deleteReadNotifications: () => api('/notifications/delete_read/', { method: 'DELETE' }),

  // Notifications — preferences
  myNotificationPreferences: () => api('/notifications/preferences/my_preferences/'),
  updateNotificationPreferences: (body: unknown) =>
    api('/notifications/preferences/update_preferences/', { method: 'PATCH', body }),

  // Notifications — templates
  notificationTemplates: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/notifications/templates/${q ? `?${q}` : ''}`);
  },
  notificationTemplate: (id: string) => api(`/notifications/templates/${id}/`),
  saveNotificationTemplate: (id: string | null, body: unknown) =>
    id
      ? api(`/notifications/templates/${id}/`, { method: 'PATCH', body })
      : api('/notifications/templates/', { method: 'POST', body }),
  previewNotificationTemplate: (id: string, context: Record<string, unknown>) =>
    api(`/notifications/templates/${id}/preview/`, { method: 'POST', body: { context } }),
  notificationTemplateMeta: () => api('/notifications/templates/available_types/'),

  // Notifications — delivery logs
  notificationDeliveryLogs: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/notifications/delivery-logs/${q ? `?${q}` : ''}`);
  },

  // Notifications — channel configs (singleton per tenant)
  emailConfig: () => api('/notifications/channels/email/'),
  saveEmailConfig: (body: unknown) =>
    api('/notifications/channels/email/', { method: 'POST', body }),
  testEmailConfig: (body: unknown) =>
    api('/notifications/channels/email/test_connection/', { method: 'POST', body }),
  resetEmailConfig: () =>
    api('/notifications/channels/email/reset/', { method: 'POST' }),
  smsConfig: () => api('/notifications/channels/sms/'),
  saveSmsConfig: (body: unknown) =>
    api('/notifications/channels/sms/', { method: 'POST', body }),
  testSmsConfig: (body: unknown) =>
    api('/notifications/channels/sms/test_connection/', { method: 'POST', body }),
  resetSmsConfig: () =>
    api('/notifications/channels/sms/reset/', { method: 'POST' }),
  whatsappConfig: () => api('/notifications/channels/whatsapp/'),
  saveWhatsappConfig: (body: unknown) =>
    api('/notifications/channels/whatsapp/', { method: 'POST', body }),
  resetWhatsappConfig: () =>
    api('/notifications/channels/whatsapp/reset/', { method: 'POST' }),
  googleConfig: () => api('/notifications/channels/google/'),
  saveGoogleConfig: (body: unknown) =>
    api('/notifications/channels/google/', { method: 'POST', body }),
  testGoogleConfig: () =>
    api('/notifications/channels/google/test_connection/', { method: 'POST' }),
  resetGoogleConfig: () =>
    api('/notifications/channels/google/reset/', { method: 'POST' }),
  createGoogleMeet: (body: unknown) =>
    api('/wellness/google-meet/create/', { method: 'POST', body }),

  // Resources
  rooms: () => api('/resources/rooms/'),
  equipment: () => api('/resources/equipment/'),
  equipmentTypes: () => api('/resources/equipment-types/'),

  // CRM — Waivers CRUD
  waivers: () => api('/crm/waivers/'),
  waiver: (id: string) => api(`/crm/waivers/${id}/`),
  createWaiver: (body: unknown) => api('/crm/waivers/', { method: 'POST', body }),
  updateWaiver: (id: string, body: unknown) => api(`/crm/waivers/${id}/`, { method: 'PATCH', body }),
  deleteWaiver: (id: string) => api(`/crm/waivers/${id}/`, { method: 'DELETE' }),

  // Comms templates CRUD
  commsTemplates: () => api('/comms/templates/'),
  commsTemplate: (id: string) => api(`/comms/templates/${id}/`),
  createCommsTemplate: (body: unknown) => api('/comms/templates/', { method: 'POST', body }),
  updateCommsTemplate: (id: string, body: unknown) => api(`/comms/templates/${id}/`, { method: 'PATCH', body }),
  deleteCommsTemplate: (id: string) => api(`/comms/templates/${id}/`, { method: 'DELETE' }),
  commsMessages: () => api('/comms/messages/'),
  // Built-in events with their default wording, variables and this
  // studio's override state — served from the backend so it cannot drift.
  commsCatalog: () => api('/comms/templates/catalog/'),

  // Staff-composed email. Rendering happens server-side so the composer
  // shows exactly what an automatic send would have produced. Members and
  // leads are separate tables, so the recipient key says which one.
  renderCommsTemplate: (id: string, body: { member?: string; lead?: string }) =>
    api(`/comms/templates/${id}/render/`, { method: 'POST', body }),
  composeAllowed: () => api('/comms/messages/compose-allowed/'),
  sendComposedEmail: (body: {
    members?: string[]; leads?: string[]; subject: string; body: string;
  }) => api('/comms/messages/send/', { method: 'POST', body }),

  // Wellness — Consultations CRUD
  createConsultation: (body: unknown) => api('/wellness/consultations/', { method: 'POST', body }),
  updateConsultation: (id: string, body: unknown) => api(`/wellness/consultations/${id}/`, { method: 'PATCH', body }),
  deleteConsultation: (id: string) => api(`/wellness/consultations/${id}/`, { method: 'DELETE' }),

  // Tenant self — Settings page save
  tenantSelf: () => api('/tenants/self/'),
  updateTenant: (body: unknown) => api('/tenants/self/update/', { method: 'PATCH', body }),
  tenantSettings: () => api('/tenants/self/settings/'),
  updateTenantSettings: (body: unknown) => api('/tenants/self/settings/', { method: 'PATCH', body }),
  // Invoice header templates (global library copied into the tenant's scope on select)
  invoiceTemplates: () => api('/tenants/self/invoice-templates/'),
  selectInvoiceTemplate: (template_key: string) =>
    api('/tenants/self/invoice-templates/select/', { method: 'POST', body: { template_key } }),
  clearInvoiceHeader: () => api('/tenants/self/invoice-templates/clear/', { method: 'DELETE' }),
  uploadInvoiceHeader: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const t = localStorage.getItem('fitboxx.staff.access');
    const res = await fetch('/api/v1/tenants/self/invoice-templates/upload/', {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
    return res.json() as Promise<{ current_header_url: string }>;
  },

  // Resources — Locations CRUD (multi-location support)
  locations: () => api('/resources/locations/'),
  location: (id: string) => api(`/resources/locations/${id}/`),
  createLocation: (body: unknown) => api('/resources/locations/', { method: 'POST', body }),
  updateLocation: (id: string, body: unknown) => api(`/resources/locations/${id}/`, { method: 'PATCH', body }),
  deleteLocation: (id: string) => api(`/resources/locations/${id}/`, { method: 'DELETE' }),

  // Gift Cards CRUD
  giftCards: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/giftcards/giftcards/${q ? `?${q}` : ''}`);
  },
  giftCard: (id: string) => api(`/giftcards/giftcards/${id}/`),
  createGiftCard: (body: unknown) => api('/giftcards/giftcards/', { method: 'POST', body }),
  updateGiftCard: (id: string, body: unknown) => api(`/giftcards/giftcards/${id}/`, { method: 'PATCH', body }),
  deleteGiftCard: (id: string) => api(`/giftcards/giftcards/${id}/`, { method: 'DELETE' }),
  redeemGiftCard: (body: unknown) => api('/giftcards/giftcards/redeem/', { method: 'POST', body }),
  cancelGiftCard: (id: string) => api(`/giftcards/giftcards/${id}/cancel/`, { method: 'POST', body: {} }),
  giftCardRedemptions: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/giftcards/redemptions/${q ? `?${q}` : ''}`);
  },

  // Biometric — Devices + Credentials + Punches + Attendance
  biometricDevices: () => api('/biometric/devices/'),
  biometricDevice: (id: string) => api(`/biometric/devices/${id}/`),
  createBiometricDevice: (body: unknown) => api('/biometric/devices/', { method: 'POST', body }),
  updateBiometricDevice: (id: string, body: unknown) => api(`/biometric/devices/${id}/`, { method: 'PATCH', body }),
  deleteBiometricDevice: (id: string) => api(`/biometric/devices/${id}/`, { method: 'DELETE' }),
  biometricCredentials: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/biometric/credentials/${q ? `?${q}` : ''}`);
  },
  createBiometricCredential: (body: unknown) => api('/biometric/credentials/', { method: 'POST', body }),
  updateBiometricCredential: (id: string, body: unknown) => api(`/biometric/credentials/${id}/`, { method: 'PATCH', body }),
  deleteBiometricCredential: (id: string) => api(`/biometric/credentials/${id}/`, { method: 'DELETE' }),
  biometricPunches: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/biometric/punches/${q ? `?${q}` : ''}`);
  },
  attendanceEvents: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/biometric/attendance/${q ? `?${q}` : ''}`);
  },
  manualCheckIn: (body: unknown) => api('/biometric/attendance/manual/', { method: 'POST', body }),

  // FinBOXX GL sync
  glAccountMaps: () => api('/finboxx/accounts/'),
  glAccountMap: (id: string) => api(`/finboxx/accounts/${id}/`),
  createGLAccountMap: (body: unknown) => api('/finboxx/accounts/', { method: 'POST', body }),
  updateGLAccountMap: (id: string, body: unknown) => api(`/finboxx/accounts/${id}/`, { method: 'PATCH', body }),
  deleteGLAccountMap: (id: string) => api(`/finboxx/accounts/${id}/`, { method: 'DELETE' }),
  glPostings: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/finboxx/postings/${q ? `?${q}` : ''}`);
  },
  retryFailedGLPostings: () => api('/finboxx/postings/retry-failed/', { method: 'POST', body: {} }),
  retryGLPosting: (id: string) => api(`/finboxx/postings/${id}/retry/`, { method: 'POST', body: {} }),

  // AI assistant
  aiThreads: () => api('/ai/threads/'),
  aiThread: (id: string) => api(`/ai/threads/${id}/`),
  createAIThread: (body: unknown) => api('/ai/threads/', { method: 'POST', body }),
  deleteAIThread: (id: string) => api(`/ai/threads/${id}/`, { method: 'DELETE' }),
  aiMessages: (threadId: string) => api(`/ai/threads/${threadId}/messages/`),
  sendAIMessage: (threadId: string, body: unknown) =>
    api(`/ai/threads/${threadId}/send/`, { method: 'POST', body }),
  aiInsights: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/ai/insights/${q ? `?${q}` : ''}`);
  },
  dismissAIInsight: (id: string) => api(`/ai/insights/${id}/dismiss/`, { method: 'POST', body: {} }),
  refreshAIInsights: () => api('/ai/insights/refresh/', { method: 'POST', body: {} }),

  // AI budget (USD cap, free + purchased credit ledger)
  aiBudget: () => api('/ai/budget/'),
  aiGateways: () => api('/ai/budget/gateways/'),
  aiPurchaseCredit: (body: {
    amount_mur?: number; amount_usd?: number; provider?: string;
    note?: string; return_url?: string; cancel_url?: string;
  }) => api('/ai/budget/purchase/', { method: 'POST', body }),
  aiAdminGrant: (body: {
    tenant_slug?: string; amount_usd?: number; kind?: string;
    confirm_grant_id?: string; external_ref?: string; note?: string;
  }) => api('/ai/budget/admin-grant/', { method: 'POST', body }),

  // Advanced Analytics
  analyticsExecutive: (days = 90) => api(`/analytics/executive/?days=${days}`),
  analyticsKPI: (days = 90) => api(`/analytics/kpi/?days=${days}`),
  analyticsHeatmap: (days = 90) => api(`/analytics/heatmap/?days=${days}`),
  analyticsRevenueTimeseries: (days = 90) => api(`/analytics/revenue-timeseries/?days=${days}`),
  analyticsInstructorLoad: (days = 90) => api(`/analytics/instructor-load/?days=${days}`),
  analyticsRetention: (days = 365) => api(`/analytics/retention/?days=${days}`),
  analyticsChurnRisk: (quiet = 30, lookBack = 180) =>
    api(`/analytics/churn-risk/?quiet_days=${quiet}&look_back=${lookBack}`),
  analyticsTopMembers: (days = 90) => api(`/analytics/top-members/?days=${days}`),
  analyticsPaymentMix: (days = 90) => api(`/analytics/payment-mix/?days=${days}`),

  // Payments — manual mark + providers catalog
  markOrderPaid: (id: string, body: unknown) =>
    api(`/payments/orders/${id}/mark-paid/`, { method: 'POST', body }),
  paymentProviders: () => api('/payments/providers/'),
  orderDetail: (id: string) => api(`/payments/orders/${id}/`),

  // Payment requests — ask a member for an amount, then confirm it arrived.
  // `create` writes NO invoice; `confirm` is the step that raises one, so
  // never settle a request through markOrderPaid — that path skips the gate.
  paymentRequests: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/payments/payment-requests/${q ? `?${q}` : ''}`);
  },
  paymentRequest: (id: string) => api(`/payments/payment-requests/${id}/`),
  createPaymentRequest: (body: unknown) =>
    api('/payments/payment-requests/', { method: 'POST', body }),
  confirmPaymentRequest: (id: string, body: unknown) =>
    api(`/payments/payment-requests/${id}/confirm/`, { method: 'POST', body }),
  cancelPaymentRequest: (id: string, body: unknown) =>
    api(`/payments/payment-requests/${id}/cancel/`, { method: 'POST', body }),
  paymentRails: () => api('/payments/payment-requests/rails/'),

  // Subscriptions (recurring billing)
  subscriptions: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/payments/subscriptions/${q ? `?${q}` : ''}`);
  },
  subscription: (id: string) => api(`/payments/subscriptions/${id}/`),
  createSubscription: (body: unknown) =>
    api('/payments/subscriptions/', { method: 'POST', body }),
  updateSubscription: (id: string, body: unknown) =>
    api(`/payments/subscriptions/${id}/`, { method: 'PATCH', body }),
  freezeSubscription: (id: string, body: unknown) =>
    api(`/payments/subscriptions/${id}/freeze/`, { method: 'POST', body }),
  resumeSubscription: (id: string) =>
    api(`/payments/subscriptions/${id}/resume/`, { method: 'POST', body: {} }),
  cancelSubscription: (id: string, body: unknown) =>
    api(`/payments/subscriptions/${id}/cancel/`, { method: 'POST', body }),
  reactivateSubscription: (id: string) =>
    api(`/payments/subscriptions/${id}/reactivate/`, { method: 'POST', body: {} }),
  billSubscriptionNow: (id: string) =>
    api(`/payments/subscriptions/${id}/bill-now/`, { method: 'POST', body: {} }),
  subscriptionEvents: (id: string) => api(`/payments/subscriptions/${id}/events/`),
  runBillingEngine: (dryRun = false) =>
    api(`/payments/billing/run/${dryRun ? '?dry_run=1' : ''}`, { method: 'POST', body: {} }),

  // Invoices — manual create / edit / mark-paid / send
  createInvoice: (body: unknown) =>
    api('/payments/invoices-edit/', { method: 'POST', body }),
  updateInvoice: (id: string, body: unknown) =>
    api(`/payments/invoices-edit/${id}/`, { method: 'PATCH', body }),
  deleteInvoice: (id: string) =>
    api(`/payments/invoices-edit/${id}/`, { method: 'DELETE' }),
  markInvoicePaid: (id: string, body: unknown) =>
    api(`/payments/invoices-edit/${id}/mark-paid/`, { method: 'POST', body }),
  sendInvoice: (id: string) =>
    api(`/payments/invoices-edit/${id}/send/`, { method: 'POST', body: {} }),

  // ── Progress Monitor — member health outcomes over time ────────────────
  // The measurable catalogue (tenant-owned).
  progressMetrics: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/progress/metrics/${q ? `?${q}` : ''}`);
  },
  createProgressMetric: (body: unknown) =>
    api('/progress/metrics/', { method: 'POST', body }),
  updateProgressMetric: (id: string, body: unknown) =>
    api(`/progress/metrics/${id}/`, { method: 'PATCH', body }),
  deleteProgressMetric: (id: string) =>
    api(`/progress/metrics/${id}/`, { method: 'DELETE' }),
  /** Install the starter catalogue for the tenant's modes. Idempotent. */
  seedProgressMetrics: () =>
    api('/progress/metrics/seed-defaults/', { method: 'POST', body: {} }),

  // Readings.
  measurements: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/progress/measurements/${q ? `?${q}` : ''}`);
  },
  createMeasurement: (body: unknown) =>
    api('/progress/measurements/', { method: 'POST', body }),
  updateMeasurement: (id: string, body: unknown) =>
    api(`/progress/measurements/${id}/`, { method: 'PATCH', body }),
  deleteMeasurement: (id: string) =>
    api(`/progress/measurements/${id}/`, { method: 'DELETE' }),
  /** One member, many metrics, one moment — how a re-assessment is taken. */
  bulkMeasurements: (body: {
    member: string; recorded_at?: string; consultation?: string | null;
    source?: string; notes?: string;
    readings: { metric: string; value: number | string; notes?: string }[];
  }) => api('/progress/measurements/bulk/', { method: 'POST', body }),

  // Goals.
  progressGoals: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/progress/goals/${q ? `?${q}` : ''}`);
  },
  createProgressGoal: (body: unknown) =>
    api('/progress/goals/', { method: 'POST', body }),
  updateProgressGoal: (id: string, body: unknown) =>
    api(`/progress/goals/${id}/`, { method: 'PATCH', body }),
  deleteProgressGoal: (id: string) =>
    api(`/progress/goals/${id}/`, { method: 'DELETE' }),
  achieveProgressGoal: (id: string) =>
    api(`/progress/goals/${id}/achieve/`, { method: 'POST', body: {} }),
  abandonProgressGoal: (id: string) =>
    api(`/progress/goals/${id}/abandon/`, { method: 'POST', body: {} }),

  // Derived views.
  memberProgress: (memberId: string, days = 365) =>
    api(`/progress/members/${memberId}/summary/?days=${days}`),
  consultationProgress: (consultationId: string) =>
    api(`/progress/consultations/${consultationId}/progress/`),
  progressOverview: (days = 90) => api(`/progress/overview/?days=${days}`),

  // ── Appearance & branding ──────────────────────────────────────────
  // The catalogue is static and public; the studio's choice is not.
  themePresets: () => api('/tenants/self/appearance/presets/'),
  appearance: () => api('/tenants/self/appearance/'),
  updateAppearance: (body: {
    theme_preset?: string;
    brand_tokens?: Record<string, string>;
    appearance?: Record<string, unknown>;
  }) => api('/tenants/self/appearance/', { method: 'PATCH', body }),
  resetAppearance: () => api('/tenants/self/appearance/reset/', { method: 'POST', body: {} }),

  // ── Notification channels ──────────────────────────────────────────
  // Status board. Credentials are edited through the /notifications/
  // channels/<kind>/ endpoints; this reports whether they WORK.
  commsChannels: () => api('/comms/channels/'),
  testCommsChannel: (body: { channel: 'email' | 'sms'; to: string }) =>
    api('/comms/channels/test/', { method: 'POST', body }),

  // ── End of Day ─────────────────────────────────────────────────────
  eodDay: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/eod/closes/day/${q ? `?${q}` : ''}`);
  },
  eodRollup: (date?: string) =>
    api(`/eod/closes/rollup/${date ? `?date=${date}` : ''}`),
  eodCloses: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/eod/closes/${q ? `?${q}` : ''}`);
  },
  eodClose: (id: string) => api(`/eod/closes/${id}/`),
  eodOpenDay: (body: { date?: string; location?: string | null }) =>
    api('/eod/closes/open/', { method: 'POST', body }),
  eodRefreshClose: (id: string) =>
    api(`/eod/closes/${id}/refresh/`, { method: 'POST', body: {} }),
  eodCount: (id: string, body: {
    counts: { denomination: string; quantity: number }[];
    petty_cash_out?: string | number;
  }) => api(`/eod/closes/${id}/count/`, { method: 'POST', body }),
  eodReconcile: (id: string, body: { notes?: string; float_carried_forward?: string | number }) =>
    api(`/eod/closes/${id}/reconcile/`, { method: 'POST', body }),
  eodReopen: (id: string, reason: string) =>
    api(`/eod/closes/${id}/reopen/`, { method: 'POST', body: { reason } }),
  eodLock: (id: string) => api(`/eod/closes/${id}/lock/`, { method: 'POST', body: {} }),
  eodAddDeposit: (id: string, body: unknown) =>
    api(`/eod/closes/${id}/deposits/`, { method: 'POST', body }),
  eodRemoveDeposit: (id: string, depositId: string) =>
    api(`/eod/closes/${id}/deposits/${depositId}/`, { method: 'DELETE' }),

  // EOD report bot.
  eodSettings: () => api('/eod/settings/'),
  updateEodSettings: (body: unknown) =>
    api('/eod/settings/update/', { method: 'PATCH', body }),
  eodRecipients: () => api('/eod/recipients/'),
  createEodRecipient: (body: unknown) => api('/eod/recipients/', { method: 'POST', body }),
  updateEodRecipient: (id: string, body: unknown) =>
    api(`/eod/recipients/${id}/`, { method: 'PATCH', body }),
  deleteEodRecipient: (id: string) => api(`/eod/recipients/${id}/`, { method: 'DELETE' }),
  eodRuns: () => api('/eod/runs/'),
  eodPreview: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/eod/runs/preview/${q ? `?${q}` : ''}`);
  },
  eodSendNow: (date?: string) =>
    api('/eod/runs/send-now/', { method: 'POST', body: date ? { date } : {} }),

  // ── Business Intelligence ──────────────────────────────────────────
  biDatasets: () => api('/bi/datasets/'),
  biReports: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/bi/reports/${q ? `?${q}` : ''}`);
  },
  biReport: (id: string) => api(`/bi/reports/${id}/`),
  createBiReport: (body: unknown) => api('/bi/reports/', { method: 'POST', body }),
  updateBiReport: (id: string, body: unknown) =>
    api(`/bi/reports/${id}/`, { method: 'PATCH', body }),
  deleteBiReport: (id: string) => api(`/bi/reports/${id}/`, { method: 'DELETE' }),
  runBiReport: (id: string, overrides: unknown = {}) =>
    api(`/bi/reports/${id}/run/`, { method: 'POST', body: overrides }),
  previewBiReport: (definition: unknown) =>
    api('/bi/reports/preview/', { method: 'POST', body: definition }),
  biReportExportUrl: (id: string, params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return `/api/v1/bi/reports/${id}/export/${q ? `?${q}` : ''}`;
  },

  biDashboards: () => api('/bi/dashboards/'),
  createBiDashboard: (body: unknown) => api('/bi/dashboards/', { method: 'POST', body }),
  updateBiDashboard: (id: string, body: unknown) =>
    api(`/bi/dashboards/${id}/`, { method: 'PATCH', body }),
  deleteBiDashboard: (id: string) => api(`/bi/dashboards/${id}/`, { method: 'DELETE' }),
  biKpis: () => api('/bi/dashboards/kpis/'),
  biKpiValues: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/bi/dashboards/kpi-values/${q ? `?${q}` : ''}`);
  },
  biKpiHistory: (kpi: string, days = 90) =>
    api(`/bi/dashboards/kpi-history/?kpi=${kpi}&days=${days}`),
  addBiWidget: (dashboardId: string, body: unknown) =>
    api(`/bi/dashboards/${dashboardId}/widgets/`, { method: 'POST', body }),
  updateBiWidget: (dashboardId: string, widgetId: string, body: unknown) =>
    api(`/bi/dashboards/${dashboardId}/widgets/${widgetId}/`, { method: 'PATCH', body }),
  deleteBiWidget: (dashboardId: string, widgetId: string) =>
    api(`/bi/dashboards/${dashboardId}/widgets/${widgetId}/`, { method: 'DELETE' }),
  reorderBiWidgets: (dashboardId: string, order: string[]) =>
    api(`/bi/dashboards/${dashboardId}/reorder/`, { method: 'POST', body: { order } }),

  biSchedules: () => api('/bi/schedules/'),
  createBiSchedule: (body: unknown) => api('/bi/schedules/', { method: 'POST', body }),
  updateBiSchedule: (id: string, body: unknown) =>
    api(`/bi/schedules/${id}/`, { method: 'PATCH', body }),
  deleteBiSchedule: (id: string) => api(`/bi/schedules/${id}/`, { method: 'DELETE' }),
  runBiSchedule: (id: string) =>
    api(`/bi/schedules/${id}/run-now/`, { method: 'POST', body: {} }),
  biScheduleRuns: (id: string) => api(`/bi/schedules/${id}/runs/`),

  // ── Cafe ───────────────────────────────────────────────────────────
  cafeMenu: () => api('/cafe/orders/menu/'),
  cafeOrders: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/cafe/orders/${q ? `?${q}` : ''}`);
  },
  cafeOrder: (id: string) => api(`/cafe/orders/${id}/`),
  openCafeOrder: (body: unknown) => api('/cafe/orders/open/', { method: 'POST', body }),
  addCafeLine: (id: string, body: {
    item: string; quantity?: number; modifiers?: string[]; note?: string;
  }) => api(`/cafe/orders/${id}/lines/`, { method: 'POST', body }),
  removeCafeLine: (id: string, lineId: string) =>
    api(`/cafe/orders/${id}/lines/${lineId}/`, { method: 'DELETE' }),
  setCafeLinePrep: (id: string, lineId: string, prep_status: string) =>
    api(`/cafe/orders/${id}/lines/${lineId}/prep/`, { method: 'PATCH', body: { prep_status } }),
  discountCafeOrder: (id: string, body: { amount: string | number; reason: string }) =>
    api(`/cafe/orders/${id}/discount/`, { method: 'POST', body }),
  tipCafeOrder: (id: string, amount: string | number) =>
    api(`/cafe/orders/${id}/tip/`, { method: 'POST', body: { amount } }),
  settleCafeOrder: (id: string, body: { method: string; reference?: string }) =>
    api(`/cafe/orders/${id}/settle/`, { method: 'POST', body }),
  closeCafeOrder: (id: string) => api(`/cafe/orders/${id}/close/`, { method: 'POST', body: {} }),
  voidCafeOrder: (id: string, reason: string) =>
    api(`/cafe/orders/${id}/void/`, { method: 'POST', body: { reason } }),
  cafePrepBoard: (station = '') =>
    api(`/cafe/orders/prep-board/${station ? `?station=${encodeURIComponent(station)}` : ''}`),
  cafeDay: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/cafe/orders/day/${q ? `?${q}` : ''}`);
  },

  cafeCategories: () => api('/cafe/categories/'),
  createCafeCategory: (body: unknown) => api('/cafe/categories/', { method: 'POST', body }),
  updateCafeCategory: (id: string, body: unknown) =>
    api(`/cafe/categories/${id}/`, { method: 'PATCH', body }),
  deleteCafeCategory: (id: string) => api(`/cafe/categories/${id}/`, { method: 'DELETE' }),
  cafeItems: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/cafe/items/${q ? `?${q}` : ''}`);
  },
  cafeItem: (id: string) => api(`/cafe/items/${id}/`),
  createCafeItem: (body: unknown) => api('/cafe/items/', { method: 'POST', body }),
  updateCafeItem: (id: string, body: unknown) =>
    api(`/cafe/items/${id}/`, { method: 'PATCH', body }),
  deleteCafeItem: (id: string) => api(`/cafe/items/${id}/`, { method: 'DELETE' }),
  adjustCafeStock: (id: string, delta: number) =>
    api(`/cafe/items/${id}/adjust-stock/`, { method: 'POST', body: { delta } }),
  cafeModifierGroups: () => api('/cafe/modifier-groups/'),
  createCafeModifierGroup: (body: unknown) =>
    api('/cafe/modifier-groups/', { method: 'POST', body }),
  updateCafeModifierGroup: (id: string, body: unknown) =>
    api(`/cafe/modifier-groups/${id}/`, { method: 'PATCH', body }),
  deleteCafeModifierGroup: (id: string) =>
    api(`/cafe/modifier-groups/${id}/`, { method: 'DELETE' }),
  // The individual options inside a group - 'Oat milk, +20'. A group with no
  // way to add answers is a question the till asks that nobody can answer.
  cafeModifiers: (groupId?: string) =>
    api(`/cafe/modifiers/${groupId ? `?group=${encodeURIComponent(groupId)}` : ''}`),
  createCafeModifier: (body: unknown) => api('/cafe/modifiers/', { method: 'POST', body }),
  updateCafeModifier: (id: string, body: unknown) =>
    api(`/cafe/modifiers/${id}/`, { method: 'PATCH', body }),
  deleteCafeModifier: (id: string) => api(`/cafe/modifiers/${id}/`, { method: 'DELETE' }),
  cafeStations: () => api('/cafe/stations/'),
  createCafeStation: (body: unknown) => api('/cafe/stations/', { method: 'POST', body }),
  updateCafeStation: (id: string, body: unknown) =>
    api(`/cafe/stations/${id}/`, { method: 'PATCH', body }),
  deleteCafeStation: (id: string) => api(`/cafe/stations/${id}/`, { method: 'DELETE' }),
  cafeSettings: () => api('/cafe/settings/'),
  updateCafeSettings: (body: unknown) =>
    api('/cafe/settings/update/', { method: 'PATCH', body }),
  cafeShifts: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/cafe/shifts/${q ? `?${q}` : ''}`);
  },
  openCafeShift: (body: { location?: string | null; opening_float?: string | number }) =>
    api('/cafe/shifts/open/', { method: 'POST', body }),
  cafeShiftExpected: (id: string) => api(`/cafe/shifts/${id}/expected/`),
  closeCafeShift: (id: string, body: { counted_cash: string | number; notes?: string }) =>
    api(`/cafe/shifts/${id}/close/`, { method: 'POST', body }),

  // ── CRM: pipeline, cases, timeline, segments ───────────────────────
  crmPipelines: () => api('/crm/pipelines/'),
  crmBoard: (pipelineId?: string) =>
    api(`/crm/pipelines/board/${pipelineId ? `?pipeline=${pipelineId}` : ''}`),
  createCrmPipeline: (body: unknown) => api('/crm/pipelines/', { method: 'POST', body }),
  crmStages: (pipelineId?: string) =>
    api(`/crm/pipeline-stages/${pipelineId ? `?pipeline=${pipelineId}` : ''}`),
  createCrmStage: (body: unknown) => api('/crm/pipeline-stages/', { method: 'POST', body }),
  updateCrmStage: (id: string, body: unknown) =>
    api(`/crm/pipeline-stages/${id}/`, { method: 'PATCH', body }),
  deleteCrmStage: (id: string) => api(`/crm/pipeline-stages/${id}/`, { method: 'DELETE' }),

  crmDeals: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/crm/deals/${q ? `?${q}` : ''}`);
  },
  crmDeal: (id: string) => api(`/crm/deals/${id}/`),
  createCrmDeal: (body: unknown) => api('/crm/deals/', { method: 'POST', body }),
  updateCrmDeal: (id: string, body: unknown) => api(`/crm/deals/${id}/`, { method: 'PATCH', body }),
  deleteCrmDeal: (id: string) => api(`/crm/deals/${id}/`, { method: 'DELETE' }),
  moveCrmDeal: (id: string, body: { stage: string; position?: number; lost_reason?: string }) =>
    api(`/crm/deals/${id}/move/`, { method: 'POST', body }),
  crmForecast: () => api('/crm/deals/forecast/'),

  crmCases: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/crm/cases/${q ? `?${q}` : ''}`);
  },
  crmCase: (id: string) => api(`/crm/cases/${id}/`),
  createCrmCase: (body: unknown) => api('/crm/cases/', { method: 'POST', body }),
  updateCrmCase: (id: string, body: unknown) => api(`/crm/cases/${id}/`, { method: 'PATCH', body }),
  deleteCrmCase: (id: string) => api(`/crm/cases/${id}/`, { method: 'DELETE' }),
  commentOnCrmCase: (id: string, body: { body: string; is_internal?: boolean }) =>
    api(`/crm/cases/${id}/comments/`, { method: 'POST', body }),
  resolveCrmCase: (id: string, resolution: string) =>
    api(`/crm/cases/${id}/resolve/`, { method: 'POST', body: { resolution } }),
  closeCrmCase: (id: string) => api(`/crm/cases/${id}/close/`, { method: 'POST', body: {} }),
  reopenCrmCase: (id: string) => api(`/crm/cases/${id}/reopen/`, { method: 'POST', body: {} }),
  crmCaseSummary: () => api('/crm/cases/summary/'),

  crmInteractions: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return api(`/crm/interactions/${q ? `?${q}` : ''}`);
  },
  createCrmInteraction: (body: unknown) =>
    api('/crm/interactions/', { method: 'POST', body }),
  updateCrmInteraction: (id: string, body: unknown) =>
    api(`/crm/interactions/${id}/`, { method: 'PATCH', body }),
  deleteCrmInteraction: (id: string) =>
    api(`/crm/interactions/${id}/`, { method: 'DELETE' }),

  crmSegments: () => api('/crm/segments/'),
  crmSegment: (id: string) => api(`/crm/segments/${id}/`),
  createCrmSegment: (body: unknown) => api('/crm/segments/', { method: 'POST', body }),
  updateCrmSegment: (id: string, body: unknown) =>
    api(`/crm/segments/${id}/`, { method: 'PATCH', body }),
  deleteCrmSegment: (id: string) => api(`/crm/segments/${id}/`, { method: 'DELETE' }),
  crmSegmentVocabulary: () => api('/crm/segments/vocabulary/'),
  previewCrmSegment: (rules: unknown) =>
    api('/crm/segments/preview/', { method: 'POST', body: rules }),
  refreshCrmSegment: (id: string) =>
    api(`/crm/segments/${id}/refresh/`, { method: 'POST', body: {} }),
  crmSegmentMembers: (id: string) => api(`/crm/segments/${id}/members/`),
};
